import type {
  ChartType,
  MetricValues,
  SecondaryDimensionValues,
} from '@revelio/core';

import { useGaChartPreviewMeta } from '../../../../global-ai-assistant/ga-chart-preview-meta-context';
import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import {
  shouldMaskAdaptiveEntityTransition,
  useAdaptiveChartType,
} from '../../../hooks/useAdaptiveChartType';
import { getRevelioPageOverrides } from '../../../utils/getRevelioPageOverrides';
import type {
  MultiMetricDataMap,
  MultiMetricProviderResult,
} from '../../DataProvider/MultiMetricProvider';
import {
  DataProviderQueryStatus,
  type DataProviderSet,
  DataType,
  isBoxBreakdownDataSet,
} from '../../DataProvider/types';
import { usePageConfig } from '../../PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../PageConfigProvider/types';
import type { PositionedItem } from '../../PageConfigProvider/types/item';
import {
  isNonHeadcountCompositionMetric,
  isPayMetric,
  isTransitionMetric,
} from '../../PageConfigProvider/types/metric';
import { Bar } from '../Bar';
import { Box as ChartBox } from '../Box';
import { ChartLoader } from '../ChartLoader';
import { ChartNotSupported } from '../ChartNotSupported';
import { Chord } from '../Chord/Chord';
import { Distribution } from '../Distribution';
import { DivergingBar } from '../DivergingBar';
import { DivergingBarCombo } from '../DivergingBarCombo';
import { Gauge } from '../Gauge';
import { GroupedBar } from '../GroupedBar';
import { Line } from '../Line';
import { Metric } from '../Metric';
import { Sankey } from '../Sankey/Sankey';
import { StackedBar } from '../StackedBar';
import { TreeMap } from '../TreeMap';
import type { ParameterSelection } from '../utils/check-datapoint-in-parameter';
import { useComputedAxis } from '../utils/compute-axis-bounds';
import { getParameterAggregatedData } from '../utils/get-parameter-aggregated-data';
import { getPrimaryParameterSelections } from '../utils/get-primary-parameter-selections';
import { groupAndFilterData } from '../utils/group-and-filter-data';
import { Volume } from '../Volume';

export const ChartSelector = ({
  chartType: baseChartType,
  data,
  item,
  metric,
  secondaryDimension,
  pageId,
  primaryParameterValues,
  queryStatus = DataProviderQueryStatus.Success,
}: {
  chartType: ChartType;
  data: DataProviderSet;
  item: PositionedItem;
  metric?: MetricValues;
  secondaryDimension?: SecondaryDimensionValues;
  pageId?: string;
  primaryParameterValues?: ParameterSelection[];
  queryStatus?: DataProviderQueryStatus;
}) => {
  const gaPreviewMeta = useGaChartPreviewMeta();
  const {
    chartType: hookChartType,
    activePrimaryFilterLength,
    isAdaptiveChartMode,
  } = useAdaptiveChartType(item);
  const previewActsAsAdaptive = gaPreviewMeta !== null;
  const adaptiveOrPreview = isAdaptiveChartMode || previewActsAsAdaptive;

  const metricShortcutPoints = (() => {
    if (
      !adaptiveOrPreview ||
      baseChartType !== 'bar' ||
      activePrimaryFilterLength !== 1 ||
      data === undefined ||
      data.type !== DataType.SINGLE
    ) {
      return null;
    }
    if (data.data.length !== 1) {
      return null;
    }
    return data.data;
  })();

  const chartType =
    metricShortcutPoints !== null ? 'metric' : (hookChartType ?? baseChartType);

  const willMaskAdaptive = shouldMaskAdaptiveEntityTransition({
    isAdaptiveChartMode,
    baseChartType,
    activePrimaryFilterLength,
    data,
    queryStatus,
  });

  if (willMaskAdaptive) {
    return <ChartLoader />;
  }

  if (chartType === 'metric' && metricShortcutPoints !== null) {
    return <Metric data={metricShortcutPoints} metric={metric} />;
  }

  if (chartType === 'gauge' && data?.type === DataType.SINGLE) {
    const gaugeMetricKey =
      (item.plot?.defaultMetricOptions?.[0] as MetricValues) ??
      ('value' as MetricValues);
    const multiData: MultiMetricDataMap = { [gaugeMetricKey]: data };
    return (
      <Gauge
        chartId={item.id}
        valueTemplate={item.plot?.gaugeValue ?? ''}
        primaryText={item.plot?.gaugePrimaryText ?? ''}
        primaryLabel={item.plot?.gaugePrimaryLabel ?? ''}
        subText={item.plot?.gaugeSubText ?? ''}
        minLabel={item.plot?.gaugeMinLabel}
        maxLabel={item.plot?.gaugeMaxLabel}
        multiData={multiData}
        colorPalette={item.plot?.colorPalette}
      />
    );
  }

  if (chartType === 'metric' && data?.type === DataType.SINGLE) {
    return <Metric data={data.data} metric={metric} />;
  }

  if (chartType === 'bar' && data?.type === DataType.SINGLE) {
    return <Bar data={data} item={item} pageId={pageId} metric={metric} />;
  }

  if (chartType === 'line' && data?.type === DataType.TIMESERIES) {
    return (
      <Line
        data={data.data}
        displayAs={item.plot?.displayAs}
        primaryParameterValues={primaryParameterValues}
      />
    );
  }

  if (chartType === 'line' && data?.type === DataType.BREAKDOWN_TIMESERIES) {
    return (
      <Line
        data={data.data.map((d) => ({
          dim: d.dim,
          timeseries: d.breakdown,
        }))}
        displayAs={item.plot?.displayAs}
        primaryParameterValues={primaryParameterValues}
      />
    );
  }

  if (
    chartType === 'sankey' &&
    (data?.type === DataType.BREAKDOWN_SINGLE ||
      data?.type === DataType.FLOW_BREAKDOWN_SINGLE)
  ) {
    return <Sankey data={data.data} />;
  }

  if (
    chartType === 'chord' &&
    (data?.type === DataType.BREAKDOWN_SINGLE ||
      data?.type === DataType.FLOW_BREAKDOWN_SINGLE)
  ) {
    return <Chord data={data.data} />;
  }

  if (
    chartType === 'treemap' &&
    (data?.type === DataType.BREAKDOWN_SINGLE || data?.type === DataType.SINGLE)
  ) {
    return <TreeMap data={data.data} metric={metric} />;
  }
  if (
    chartType === 'stacked-bar' &&
    (data?.type === DataType.BREAKDOWN_SINGLE ||
      data?.type === DataType.BREAKDOWN_TIMESERIES)
  ) {
    return (
      <StackedBar
        data={data}
        metric={metric}
        hideLegend={
          data.type === DataType.BREAKDOWN_SINGLE &&
          isTransitionMetric(metric) &&
          [
            RevelioPageId.COMPANY_TRANSITIONS,
            RevelioPageId.GEOGRAPHY_TRANSITIONS,
            RevelioPageId.ROLE_TRANSITIONS,
            RevelioPageId.COMPANY_TRANSITIONS_V2,
            RevelioPageId.GEOGRAPHY_TRANSITIONS_V2,
            RevelioPageId.ROLE_TRANSITIONS_V2,
          ].includes(pageId as RevelioPageId)
        }
      />
    );
  }
  if (chartType === 'grouped-bar' && data?.type === DataType.BREAKDOWN_SINGLE) {
    return (
      <GroupedBar
        data={data.data}
        hideLegend={[
          RevelioPageId.ROLE_POSTINGS,
          RevelioPageId.GEOGRAPHY_POSTINGS,
          RevelioPageId.COMPANY_POSTINGS,
        ].includes(pageId as RevelioPageId)}
        ignoreCustomTooltip={
          isPayMetric(metric) || isNonHeadcountCompositionMetric(metric)
        }
      />
    );
  }
  if (
    chartType === 'diverging-bar' &&
    data?.type === DataType.BREAKDOWN_SINGLE
  ) {
    if (data.data.length !== 2) {
      return (
        <ChartNotSupported
          errorDetails={{
            chartId: item.id,
            chartTitle: item.title ?? undefined,
            chartType,
            metric,
            secondaryDimension,
            reason: 'chart_not_supported',
          }}
        >
          Diverging bar requires exactly two entities selected.
        </ChartNotSupported>
      );
    }
    return <DivergingBar dataSet={data} />;
  }
  if (chartType === 'diverging-bar' && data?.type === DataType.SINGLE) {
    return <DivergingBar dataSet={data} />;
  }
  if (chartType === 'volume' && data?.type === DataType.TIMESERIES) {
    return <Volume data={data.data} />;
  }
  if (
    data?.type === DataType.BOX &&
    chartType === 'box' &&
    isBoxBreakdownDataSet(data)
  ) {
    return (
      <ChartBox
        data={data.data}
        primaryParameterValues={primaryParameterValues}
      />
    );
  }
  if (data?.type === DataType.DISTRIBUTION && chartType === 'distribution') {
    const { colorOverride } = getRevelioPageOverrides(item);

    return <Distribution data={data.data} colorOverride={colorOverride} />;
  }
  if (
    chartType === 'diverging-bar-combo' &&
    data?.type === DataType.BREAKDOWN_SINGLE
  ) {
    if (data.data.length !== 2) {
      return (
        <ChartNotSupported
          errorDetails={{
            chartId: item.id,
            chartTitle: item.title ?? undefined,
            chartType,
            metric,
            secondaryDimension,
            reason: 'chart_not_supported',
          }}
        >
          Diverging bar combo requires exactly two entities selected.
        </ChartNotSupported>
      );
    }
    return <DivergingBarCombo dataSet={data} />;
  }

  return (
    <ChartNotSupported
      errorDetails={{
        chartId: item.id,
        chartTitle: item.title ?? undefined,
        chartType,
        metric,
        secondaryDimension,
        reason: 'chart_not_supported',
      }}
    />
  );
};

export const MultiMetricBodyChartSelector = ({
  item,
  chartType,
  data,
  metric,
}: {
  item: PositionedItem;
  chartType: ChartType;
  data: MultiMetricProviderResult;
  metric?: MetricValues;
}) => {
  const { config: pageConfig } = usePageConfig();
  const { activeFilterMap } = useFilterContext();

  const multiDataForAxis: MultiMetricDataMap | undefined =
    chartType === 'line' && data.byMetric
      ? (() => {
          const reportParameters =
            pageConfig.tabs?.[0]?.tabContent?.reportParameters ?? [];
          const plotParameters = item?.plot?.plotParameters ?? [];
          const out: MultiMetricDataMap = {};
          (Object.keys(data.byMetric) as MetricValues[]).forEach(
            (metricKey) => {
              const dataset = data.byMetric[metricKey];
              if (dataset) {
                out[metricKey] = getParameterAggregatedData({
                  data: dataset,
                  plotParameters,
                  reportParameters,
                  activeFilterMap,
                });
              }
            }
          );
          return out;
        })()
      : undefined;

  const computedYAxis = useComputedAxis({
    yAxisMin: item?.plot?.yAxisMin,
    yAxisMax: item?.plot?.yAxisMax,
    multiData: multiDataForAxis,
  });

  if (chartType === 'gauge') {
    const gaugeValue = item.plot?.gaugeValue ?? '';
    const gaugePrimaryText = item.plot?.gaugePrimaryText ?? '';
    const gaugePrimaryLabel = item.plot?.gaugePrimaryLabel ?? '';
    const gaugeSubText = item.plot?.gaugeSubText ?? '';
    return (
      <Gauge
        chartId={item.id}
        valueTemplate={gaugeValue}
        primaryText={gaugePrimaryText}
        primaryLabel={gaugePrimaryLabel}
        subText={gaugeSubText}
        minLabel={item.plot?.gaugeMinLabel}
        maxLabel={item.plot?.gaugeMaxLabel}
        multiData={data.byMetric}
        colorPalette={item.plot?.colorPalette}
      />
    );
  }

  if (chartType === 'line' && data.byMetric && multiDataForAxis) {
    const lineMetric: MetricValues | undefined =
      metric ?? item.plot?.defaultMetricOptions?.[0];
    const reportParameters =
      pageConfig.tabs?.[0]?.tabContent?.reportParameters ?? [];
    const plotParameters = item?.plot?.plotParameters ?? [];

    const dataset = lineMetric ? data.byMetric[lineMetric] : undefined;

    if (dataset) {
      const aggregatedData = getParameterAggregatedData({
        data: dataset,
        plotParameters,
        reportParameters,
        activeFilterMap,
      });
      const { groupBySegment, plotFilters } = item?.plot ?? {};
      const filteredData = groupAndFilterData({
        data: aggregatedData,
        groupBySegment,
        plotFilters,
      });
      const primaryParams = getPrimaryParameterSelections({
        reportParameters,
        activeFilterMap,
      });

      if (filteredData?.type === DataType.TIMESERIES) {
        return (
          <Line
            data={filteredData.data}
            displayAs={item.plot?.displayAs}
            primaryParameterValues={primaryParams}
            yAxis={computedYAxis}
          />
        );
      }

      if (filteredData?.type === DataType.BREAKDOWN_TIMESERIES) {
        return (
          <Line
            data={filteredData.data.map((d) => ({
              dim: d.dim,
              timeseries: d.breakdown,
            }))}
            displayAs={item.plot?.displayAs}
            primaryParameterValues={primaryParams}
            yAxis={computedYAxis}
          />
        );
      }
    }
  }

  return (
    <ChartNotSupported
      errorDetails={{
        chartId: item.id,
        chartTitle: item.title ?? undefined,
        chartType,
        reason: 'chart_not_supported',
      }}
    />
  );
};
